import {SpringPageable} from "@variocube/app-ui";
import {useMemo} from "react";
import {TypeOf} from "zod";
import {useAccessToken} from "../auth";
import {useClientId} from "../ClientIdProvider";
import {useTenantId} from "../tenant";
import {createApiClientWithBaseConfig} from "./client";
import {
	CompartmentCreation,
	CompartmentMutation,
	CompartmentStatus,
	get_QueryCompartments,
	LockStatus,
} from "./client.gen";

export function useCompartmentApi() {
	const accessToken = useAccessToken();
	const tenantId = useTenantId();
	const clientId = useClientId();
	return useMemo(() => createCompartmentApi(tenantId, clientId, accessToken), [tenantId, accessToken]);
}

export type CompartmentQuery = TypeOf<get_QueryCompartments["parameters"]>["query"];

export const COMPARTMENT_STATUSES = CompartmentStatus.options.map(option => option.value);
export const LOCK_STATUSES = LockStatus.options.map(option => option.value);

function createCompartmentApi(tenantId: string, clientId: string | undefined, accessToken: string | undefined) {
	const {api, withConfig} = createApiClientWithBaseConfig(clientId, accessToken);
	return {
		createCompartment: (compartmentCreation: CompartmentCreation) =>
			api.post(
				"/api/tenants/{tenantId}/compartments",
				withConfig({
					path: {tenantId},
					body: compartmentCreation,
				}),
			),
		updateCompartment: (compartmentId: number, compartmentMutation: CompartmentMutation) =>
			api.put(
				"/api/tenants/{tenantId}/compartments/{compartmentId}",
				withConfig({
					path: {tenantId, compartmentId},
					body: compartmentMutation,
				}),
			),
		getCompartment: (compartmentId: number) =>
			api.get(
				"/api/tenants/{tenantId}/compartments/{compartmentId}",
				withConfig({
					path: {tenantId, compartmentId},
				}),
			),
		deleteCompartment: (compartmentId: number) =>
			api.delete(
				"/api/tenants/{tenantId}/compartments/{compartmentId}",
				withConfig({
					path: {tenantId, compartmentId},
				}),
			),
		queryCompartments: (query: CompartmentQuery) =>
			api.get(
				"/api/tenants/{tenantId}/compartments",
				withConfig({
					path: {tenantId},
					query,
				}),
			),
		getCompartmentSizes: () =>
			api.get(
				"/api/tenants/{tenantId}/compartments/sizes",
				withConfig({
					path: {tenantId},
				}),
			),
		getCompartmentBookings: (compartmentId: number, pageable: SpringPageable) =>
			api.get(
				"/api/tenants/{tenantId}/compartments/{compartmentId}/bookings",
				withConfig({
					path: {tenantId, compartmentId},
					query: pageable,
				}),
			),
		getActiveCompartmentBooking: async (compartmentId: number) => {
			const page = await api.get(
				"/api/tenants/{tenantId}/compartments/{compartmentId}/bookings",
				withConfig({
					path: {tenantId, compartmentId},
					query: {status: "ACTIVE" as const, size: 1, sort: ["from,desc"]},
				}),
			);
			return page.content?.[0];
		},
		getCompartmentAuditLog: (compartmentId: number, pageable: SpringPageable) =>
			api.get(
				"/api/tenants/{tenantId}/compartments/{compartmentId}/audit",
				withConfig({
					path: {tenantId, compartmentId},
					query: pageable,
				}),
			),
		assignCompartmentLock: (compartmentId: number, lockId: string | undefined) =>
			api.put(
				"/api/tenants/{tenantId}/compartments/{compartmentId}/lock",
				withConfig({
					path: {tenantId, compartmentId},
					body: {lockId},
				}),
			),
		assignCompartmentLocation: (compartmentId: number, locationId: number) =>
			api.put(
				"/api/tenants/{tenantId}/compartments/{compartmentId}/location",
				withConfig({
					path: {tenantId, compartmentId},
					body: {locationId},
				}),
			),
	};
}
